import { motion } from "framer-motion";

const aiwooxExperiences = [
  {
    role: "DevOps Engineer — DebateHQ",
    type: "Debate Platform",
    duration: "Feb 2025 – Present",
    badge: "Current",
    badgeColor: "bg-green-500/10 border-green-500/30 text-green-400",
    dotColor: "bg-green-500",
    desc: "Comprehensive debate platform built with Angular 19, TypeScript, TailwindCSS, Supabase, Socket.io, and Capacitor for mobile support.",
    responsibilities: [
      "Worked across frontend, backend, and database layers for end-to-end functionality",
      "Implemented WebRTC peer-to-peer connections for real-time communication",
      "Automated build, test, and deployment workflows using GitHub Actions CI/CD",
      "Deployed using Docker, CI/CD pipelines, VPS infrastructure, and Kubernetes (K8s)",
      "Managed email server config, implemented SSO, and tested APIs with Postman",
      "Configured DNS records and SSL certificates for secure application access",
      "Secured VPS against malware and unauthorized access with system hardening",
      "Monitored server logs, app performance, and system health for production stability",
    ],
    tags: ["Angular 19", "Docker", "Kubernetes", "GitHub Actions", "VPS", "Supabase", "WebRTC", "SSL"],
  },
  {
    role: "DevOps Engineer — GoReservee",
    type: "Hotel Booking Platform",
    duration: "Aiwoox",
    badge: "Production",
    badgeColor: "bg-sky-500/10 border-sky-500/30 text-sky-400",
    dotColor: "bg-sky-500",
    desc: "Multi-service travel booking platform for hotels, flights, tours, cabs, and restaurants with real-time availability and AWS infrastructure.",
    responsibilities: [
      "Designed and deployed scalable infrastructure using Amazon EC2 for frontend and backend",
      "Configured Amazon RDS (PostgreSQL/MySQL) for secure, highly available database management",
      "Implemented authentication using Amazon Cognito with OTP-based login and RBAC",
      "Utilized Amazon S3 for storing images, user uploads, and static assets securely",
      "Containerized applications using Docker and deployed with NGINX reverse proxy",
      "Built and automated CI/CD pipelines using GitHub Actions for seamless deployments",
      "Configured domain mapping and DNS via GoDaddy with SSL certificates for HTTPS",
    ],
    tags: ["Angular 18", "AWS EC2", "RDS", "Cognito", "S3", "Docker", "NGINX", "GitHub Actions"],
  },
  {
    role: "DevOps Engineer — Stock Analysis Platform",
    type: "Trading Analytics Platform",
    duration: "Aiwoox",
    badge: "Production",
    badgeColor: "bg-purple-500/10 border-purple-500/30 text-purple-400",
    dotColor: "bg-purple-500",
    desc: "Next.js-based stock analysis platform with AWS S3 file storage, Firebase real-time data, P&L analytics, and win-rate calculations.",
    responsibilities: [
      "Built full-stack trading analytics platform using Next.js and TypeScript",
      "Implemented real-time P&L calculations, win-rate metrics, and strategy management",
      "Configured AWS IAM roles and policies for secure, least-privilege access",
      "Integrated AWS S3 for scalable file storage with presigned URLs via AWS SDK v3",
      "Deployed on AWS EC2, managing server configuration and production availability",
    ],
    tags: ["Next.js", "AWS S3", "EC2", "IAM", "Firebase", "TypeScript", "AWS SDK v3"],
  },
];

const freelanceProjects = [
  {
    title: "Stock Market Analysis Tool",
    link: "https://stockanalysis.abholdings.info/",
    linkLabel: "stockanalysis.abholdings.info",
    color: "border-orange-500/30 bg-orange-500/5",
    accentColor: "text-orange-400",
    desc: "AI-assisted full-stack stock market analytics platform. Upload stock market files, analyze profit/loss, identify strategies, and track entry/exit points.",
    responsibilities: [
      "Built frontend & backend using AI-assisted development for rapid delivery",
      "Upload CSV/XLSX stock market files — analyzes profit days, loss days, P&L",
      "Tracks strategy performance — Range OLC, Range HAL, Open & Close signals",
      "Supports NIFTY, NSE, BSE, Reliance, Adani, and other company data",
      "Deployed on AWS EC2 with VPC, S3 storage, and RDS for data persistence",
      "AWS cost optimization — right-sized instances, S3 lifecycle policies",
      "SSL certificate, custom domain, and server kept live 24/7",
    ],
    tags: ["Next.js", "AWS EC2", "S3", "RDS", "Firebase", "AI-Assisted", "SSL"],
  },
  {
    title: "Instagram Analytics Tool",
    link: "https://instagramanalysis.abholdings.info/",
    linkLabel: "instagramanalysis.abholdings.info",
    color: "border-pink-500/30 bg-pink-500/5",
    accentColor: "text-pink-400",
    desc: "Instagram public profile analytics tool. Add any public profile, upload posts via Excel sheet — analyzes likes, views, comments, and post performance.",
    responsibilities: [
      "Built frontend & backend using AI-assisted development",
      "Add Instagram public profile and upload post data via Excel sheet",
      "Analyzes how many likes, views, comments each post received",
      "Shows which team member uploaded the post and post performance trends",
      "Deployed via API integration on AWS with cost-optimized infrastructure",
      "Custom domain with SSL, NGINX reverse proxy, and 24/7 uptime",
    ],
    tags: ["React", "Node.js", "AWS", "NGINX", "API Integration", "Excel Upload", "SSL"],
  },
  {
    title: "Instagram Post Excel Analyzer",
    link: "https://instagramtool.abholdings.info/",
    linkLabel: "instagramtool.abholdings.info",
    color: "border-purple-500/30 bg-purple-500/5",
    accentColor: "text-purple-400",
    desc: "Upload Instagram post Excel sheets — analyzes date range, identifies which team member posted, tracks views, likes, and provides data analysis insights.",
    responsibilities: [
      "Upload Instagram post Excel sheets for bulk data analysis",
      "Analyzes date range and filters posts by team member",
      "Shows how many views, likes, and comments per post",
      "Helps with data analysis and performance reporting",
      "Deployed on AWS with API-driven architecture",
      "Cost-optimized server running continuously with monitoring",
    ],
    tags: ["React", "AWS", "API", "Data Analysis", "Excel Processing", "Node.js"],
  },
  {
    title: "Stock Trend Tracker",
    link: "https://teststocktrend.abholdings.info/",
    linkLabel: "teststocktrend.abholdings.info",
    color: "border-cyan-500/30 bg-cyan-500/5",
    accentColor: "text-cyan-400",
    desc: "Stock trend analysis platform built with React frontend. Uses Firebase/Supabase for data management, deployed on AWS EC2, VPC, S3, and RDS.",
    responsibilities: [
      "Built entirely with React frontend using AI-assisted development",
      "Application data managed via Firebase and Supabase",
      "Deployed on AWS — EC2, VPC, S3, RDS for full cloud infrastructure",
      "API integrations for real-time stock data fetching and processing",
      "AWS cost optimization — auto-scaling, reserved instances, S3 tiering",
      "SSL, custom domain, DNS configuration, and server always running",
    ],
    tags: ["React", "Firebase", "Supabase", "AWS EC2", "VPC", "S3", "RDS", "API"],
  },
];

function AiwooxCard({ exp, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15, duration: 0.6 }}
      className="relative pl-6 border-l-2 border-sky-500/30"
    >
      <div className={`absolute -left-[9px] top-2 w-4 h-4 rounded-full ${exp.dotColor} shadow-lg`} />
      <div className="bg-[#0d1526] border border-sky-500/15 rounded-2xl p-4 sm:p-6 ml-2 card-hover w-full min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <h3 className="text-white font-bold text-base sm:text-lg leading-snug">{exp.role}</h3>
            <p className="gradient-text font-semibold text-sm">Aiwoox</p>
            <p className="text-slate-500 text-xs mt-0.5">{exp.type}</p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className={`border text-xs font-semibold px-3 py-1 rounded-full ${exp.badgeColor}`}>
              {exp.badge}
            </span>
            <span className="text-slate-500 text-xs">{exp.duration}</span>
          </div>
        </div>
        <p className="text-slate-400 text-sm leading-relaxed my-3 border-l-2 border-sky-500/30 pl-3">
          {exp.desc}
        </p>
        <ul className="space-y-1.5 mb-4">
          {exp.responsibilities.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-slate-400 text-sm">
              <span className="text-sky-400 mt-0.5 shrink-0">▹</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2">
          {exp.tags.map((t) => (
            <span key={t} className="bg-slate-800/80 text-slate-300 text-xs px-2 py-1 rounded-md border border-slate-700/50">
              {t}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function FreelanceCard({ proj, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      className={`border rounded-2xl p-4 sm:p-6 card-hover w-full min-w-0 ${proj.color}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-white font-bold text-base leading-snug">{proj.title}</h3>
          <a
            href={proj.link}
            target="_blank"
            rel="noreferrer"
            className={`text-xs font-semibold ${proj.accentColor} hover:underline mt-0.5 block truncate`}
          >
            {proj.linkLabel}
          </a>
        </div>
        <span className="bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap shrink-0">
          Live
        </span>
      </div>
      <p className="text-slate-400 text-sm leading-relaxed mb-4 border-l-2 border-slate-600 pl-3">
        {proj.desc}
      </p>
      <ul className="space-y-1.5 mb-4">
        {proj.responsibilities.map((r, i) => (
          <li key={i} className="flex items-start gap-2 text-slate-400 text-sm">
            <span className={`${proj.accentColor} mt-0.5 shrink-0`}>▹</span>
            <span>{r}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {proj.tags.map((t) => (
          <span key={t} className="bg-slate-800/80 text-slate-300 text-xs px-2 py-1 rounded-md border border-slate-700/50">
            {t}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

export default function Experience() {
  return (
    <section id="experience" className="py-16 md:py-24 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-sky-400 font-semibold uppercase tracking-widest text-sm mb-2">My Journey</p>
          <h2 className="text-4xl md:text-5xl font-black text-white">Experience</h2>
          <div className="w-16 h-1 bg-sky-500 mx-auto mt-4 rounded-full" />
        </motion.div>

        {/* Aiwoox */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-xs font-black text-sky-400 shrink-0">
              AW
            </div>
            <div>
              <h3 className="text-white font-black text-lg sm:text-xl">Aiwoox</h3>
              <p className="text-slate-400 text-sm">Full-Time · DevOps Engineer</p>
            </div>
          </motion.div>
          <div className="space-y-6">
            {aiwooxExperiences.map((exp, i) => (
              <AiwooxCard key={exp.role} exp={exp} index={i} />
            ))}
          </div>
        </div>

        {/* Freelance */}
        <div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center justify-center text-xs font-black text-green-400 shrink-0">
              FL
            </div>
            <div>
              <h3 className="text-white font-black text-lg sm:text-xl">Freelance DevOps Engineer</h3>
              <p className="text-slate-400 text-sm">Self-Employed · AI-Assisted Dev & AWS Deployment</p>
            </div>
          </motion.div>

          {/* Freelance highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#0d1526] border border-green-500/20 rounded-2xl p-4 sm:p-5 mb-6"
          >
            <p className="text-slate-300 text-sm font-semibold mb-3">What I do as a Freelancer</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                "Build frontend & backend using AI-assisted development",
                "Deploy applications on AWS — EC2, VPC, S3, RDS",
                "AWS cost optimization & server performance tuning",
                "API integrations for deploying data-driven applications",
                "SSL certificates, DNS config & custom domain setup",
                "Server monitoring & keeping apps live 24/7",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="text-green-400 shrink-0 mt-0.5">▹</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <p className="text-slate-400 text-sm font-semibold uppercase tracking-wider mb-4">
            Live Freelance Projects
          </p>
          <div className="grid gap-5">
            {freelanceProjects.map((proj, i) => (
              <FreelanceCard key={proj.title} proj={proj} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
